import { type CrowdexAssistant, createAssistantInputSchema } from '@crowdex/agent-contracts/api';
import type { WorkspaceDirectoryApi, WorkspaceDirectorySnapshot } from './directory-client';

/**
 * A directory kept in memory, for `/desktop-preview` (no server, no sign-in) and tests: it starts
 * from a snapshot and answers every call as the server would, without routing (lot 6).
 */
export function memoryWorkspaceDirectoryApi(
  initial: WorkspaceDirectorySnapshot,
): WorkspaceDirectoryApi {
  const state: WorkspaceDirectorySnapshot = structuredClone(initial);
  const now = () => new Date().toISOString();
  const id = () => globalThis.crypto.randomUUID();
  const projectOf = (projectId: string) => {
    const found = state.projects.find((entry) => entry.id === projectId);
    if (found === undefined) throw new Error('No such assistant.');
    return found;
  };
  // An avatar serves one assistant: choosing it for another takes it from the first.
  const takeAvatar = (avatarId: string | null, except?: string) => {
    if (avatarId === null) return;
    for (const entry of state.projects)
      if (entry.avatarId === avatarId && entry.id !== except) entry.avatarId = null;
  };
  return {
    list: async () => structuredClone(state),
    async createAssistant(input) {
      const parsed = createAssistantInputSchema.parse(input);
      takeAvatar(parsed.avatarId ?? null);
      const project: CrowdexAssistant = {
        id: id(),
        // The server still files it somewhere; nothing reads it (docs/adr-assistants.md).
        workspaceId: state.projects.find((entry) => entry.isHome === true)?.workspaceId ?? 'home',
        name: parsed.name,
        goal: parsed.goal,
        color: parsed.color,
        repositoryIds: parsed.repositoryIds,
        memberAgentIds: parsed.memberAgentIds,
        avatarId: parsed.avatarId ?? null,
        isHome: false,
        workflowIds: [],
        automationIds: parsed.automationIds ?? [],
        defaultAutomation: parsed.defaultAutomation ?? null,
        wakes: [],
        instructions: '',
        conversationId: id(),
        archivedAt: null,
        createdAt: now(),
        updatedAt: now(),
      };
      state.projects.push(project);
      return structuredClone(project);
    },
    async updateAssistant(projectId, input) {
      const { archived, ...fields } = input;
      if (archived === true && projectOf(projectId).isHome === true)
        throw new Error('The home assistant stays.');
      if (fields.avatarId !== undefined) takeAvatar(fields.avatarId, projectId);
      const project = Object.assign(projectOf(projectId), fields, {
        ...(archived === undefined ? {} : { archivedAt: archived ? now() : null }),
        updatedAt: now(),
      });
      return structuredClone(project);
    },
    async moveConversation(conversationId, input) {
      if (input.projectId !== null) projectOf(input.projectId);
      const current = state.placements[conversationId];
      // As the server: a child stays under its parent within the same project, unless detached.
      const parentProject =
        current?.parentConversationId == null
          ? null
          : (state.placements[current.parentConversationId]?.projectId ??
            state.projects.find((entry) => entry.conversationId === current.parentConversationId)
              ?.id ??
            null);
      const keepsParent =
        input.parentConversationId !== null &&
        input.projectId !== null &&
        parentProject === input.projectId;
      const placement = {
        workflowRun: null,
        workspaceId: null,
        ...current,
        projectId: input.projectId,
        parentConversationId: keepsParent ? (current?.parentConversationId ?? null) : null,
      };
      state.placements[conversationId] = placement;
      return structuredClone(placement);
    },
    assistantTasks: async () => [],
    async openProjectConversation(projectId) {
      const project = projectOf(projectId);
      project.conversationId ??= id();
      return project.conversationId;
    },
  };
}
